import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import SharedChatEntryPoint from '@databox/ember-integrations/lib/components/Ai/SharedChat/SharedChatEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode, redirect } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

interface SharedChatSignature {
	Element: HTMLDivElement;
	Args: {
		shareToken: string;
	};
}

export default class GenieSharedChatComponent extends Component<SharedChatSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);

		schedule('afterRender', () => {
			next(() => {
				this.renderSharedChat();
			});
		});

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderSharedChat);
		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderSharedChat);
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
	}

	@action
	navigateToGenie(threadId?: string) {
		if (threadId) {
			this.navigationService.transitionToRoute('genie.chat.thread', threadId);
		} else {
			this.navigationService.transitionToRoute('genie');
		}
	}

	@action
	navigateToLogin() {
		if (this.navigationService.isMobileApp) {
			redirect('/m/logout');
		} else {
			redirect('/logout');
		}
	}

	private renderSharedChat() {
		const isAuthenticated = Boolean(this.currentUserService.accessToken);

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken,
			dataToken,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			shareToken: this.args.shareToken,
			isAuthenticated,
			isWhitelabeled: this.accountService.whiteLabelingEnabled,
			userName: this.currentUserService?.model?.firstName,
			onStartConversation: isAuthenticated ? this.navigateToGenie : undefined,
			onTryYourself: !isAuthenticated ? this.navigateToLogin : undefined,
			onLogin: !isAuthenticated ? this.navigateToLogin : undefined,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				SharedChatEntryPoint(props),
				getShadowDomMode(this.accountService?.email),
			);
		}
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="shared-chat-container"
			class="shared-chat-container"
			...attributes
		/>
	</template>
}
